// Create account (WEB_PLAN.md §4.2, §5.1), the web twin of iOS SignInView in create-account mode: Apple / Google,
// then email + password + confirmation with the live policy checklist. With confirmations on (the project's
// setting) GoTrue emails a 6-digit code and /verify finishes the sign-up. An address that already has a confirmed
// account gets a look-alike answer with no identities and no email: say so right away and offer Sign in.
// While the server takes no new accounts, the card says so instead (signing in still works).

import { UserRoundPlus, UserX } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthOptions } from '@/auth/authSettings';
import { authErrorCode, authErrorMessage, isOptionTurnedOff } from '@/auth/errors';
import { isEmailLike, signUpWithEmail } from '@/auth/flows';
import { isValidPassword } from '@/auth/passwordPolicy';
import { recalledEmail, rememberEmail } from '@/auth/pending';
import { AuthCardBody, AuthHeader, AuthPage } from './components/AuthCard';
import { AuthModeSwitch } from './components/AuthModeSwitch';
import { PasswordChecklist } from './components/PasswordChecklist';
import { PasswordNotSavedNotice } from './components/PasswordNotSavedNotice';
import { ProviderButtons, type ProviderBusy } from './components/ProviderButtons';
import {
  Caption,
  ErrorText,
  Field,
  OrDivider,
  PasswordInput,
  PrimaryButton,
  PrivacyConsentLine,
  SecondaryButton,
  TextInput,
} from './components/ui';

type Busy = 'form' | Exclude<ProviderBusy, null> | null;

export default function SignUpPage() {
  const navigate = useNavigate();
  const options = useAuthOptions();
  const [email, setEmail] = useState(() => recalledEmail());
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [providerError, setProviderError] = useState<string | null>(null);
  /** The address already has a confirmed account: offer "Sign in instead" under the error. */
  const [accountExists, setAccountExists] = useState(false);
  /** A server that confirms by itself answered with a session whose password couldn't be saved again. */
  const [unsaved, setUnsaved] = useState(false);

  const signUpsOff = options.known && !options.signUp;
  const showsProviders = options.apple || options.google;
  const canCreate = isEmailLike(email) && isValidPassword(password) && confirmation === password && busy === null;

  const submit = async () => {
    if (!canCreate) return;
    setBusy('form');
    setError(null);
    setProviderError(null);
    setAccountExists(false);
    try {
      const result = await signUpWithEmail(email, password);
      setPassword('');
      setConfirmation('');
      if (result === 'needsVerification') {
        navigate('/verify');
      } else if (result === 'unsaved') {
        setUnsaved(true);
        setBusy(null);
      }
      // 'saved': signed in right away; AuthProvider takes over. Stay busy meanwhile.
    } catch (e) {
      const code = authErrorCode(e);
      setAccountExists(code === 'user_already_exists' || code === 'email_exists');
      setError(authErrorMessage(e));
      if (isOptionTurnedOff(e)) options.refresh();
      setBusy(null);
    }
  };

  const signInInstead = () => {
    rememberEmail(email);
    navigate('/sign-in', { replace: true });
  };

  return (
    <AuthPage>
      <AuthHeader
        icon={<UserRoundPlus className="h-6 w-6" aria-hidden />}
        title="Create your account"
        subtitle="Then create your company, or join your team with an invitation."
      />
      {unsaved ? (
        <AuthCardBody>
          <PasswordNotSavedNotice />
        </AuthCardBody>
      ) : (
        <>
          <AuthModeSwitch mode="signUp" disabled={busy !== null} />
          {signUpsOff ? (
            <AuthCardBody>
              <p className="flex items-start gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                <UserX className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                New accounts are turned off on this server.
              </p>
              <SecondaryButton onClick={signInInstead}>Sign in instead</SecondaryButton>
            </AuthCardBody>
          ) : (
            <>
              <AuthCardBody as="form" onSubmit={() => void submit()}>
                <ProviderButtons
                  mode="signUp"
                  apple={options.apple}
                  google={options.google}
                  busy={busy === 'form' ? null : busy}
                  disabled={busy === 'form'}
                  onBusyChange={(b) => setBusy(b)}
                  onError={(message) => {
                    setError(null);
                    setAccountExists(false);
                    setProviderError(message);
                  }}
                  onOptionTurnedOff={options.refresh}
                />
                <ErrorText>{providerError}</ErrorText>
                {options.email && (
                  <>
                    {showsProviders && <OrDivider />}
                    <Field label="Email">
                      {(id) => (
                        <TextInput
                          id={id}
                          type="email"
                          inputMode="email"
                          autoComplete="username"
                          autoCapitalize="off"
                          autoCorrect="off"
                          spellCheck={false}
                          placeholder="name@company.com"
                          aria-label="Email address"
                          value={email}
                          disabled={busy !== null}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            rememberEmail(e.target.value);
                          }}
                        />
                      )}
                    </Field>
                    <Field label="Password">
                      {(id) => (
                        <PasswordInput
                          id={id}
                          revealed={revealed}
                          onToggleReveal={() => setRevealed((r) => !r)}
                          autoComplete="new-password"
                          placeholder="Password"
                          value={password}
                          disabled={busy !== null}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                      )}
                    </Field>
                    <Field label="Confirm password">
                      {(id) => (
                        <PasswordInput
                          id={id}
                          revealed={revealed}
                          showsRevealButton={false}
                          autoComplete="new-password"
                          placeholder="Confirm password"
                          value={confirmation}
                          disabled={busy !== null}
                          onChange={(e) => setConfirmation(e.target.value)}
                        />
                      )}
                    </Field>
                    <PasswordChecklist password={password} confirmation={confirmation} />
                    <ErrorText>{error}</ErrorText>
                    {accountExists && (
                      <SecondaryButton onClick={signInInstead} disabled={busy !== null}>
                        Sign in instead
                      </SecondaryButton>
                    )}
                    <PrimaryButton
                      type="submit"
                      busy={busy === 'form'}
                      busyLabel="Creating account…"
                      icon={<UserRoundPlus className="h-4 w-4" aria-hidden />}
                      disabled={!canCreate}
                    >
                      Create account
                    </PrimaryButton>
                    <Caption>We'll email you a 6-digit code to confirm it's your address.</Caption>
                  </>
                )}
              </AuthCardBody>
              <PrivacyConsentLine />
            </>
          )}
        </>
      )}
    </AuthPage>
  );
}
